/**
 * 页面跳转目标解析(真实 URL 纪律:登录后回到原始 return_to;禁止
 * 客户端内存态假装页面)。全部产出同源绝对路径(SitePath)。
 */
import type { SitePath } from '@web-v4/api-contract/v1';
import type { MeData } from '@web-v4/api-contract/v1/auth';
import { appUrl } from '@web-v4/router-utils';

/**
 * 从当前地址栏 query 解析 return_to。只接受同源绝对路径
 * ('/' 开头且非 '//' —— '//evil.example' 是协议相对外链,拒绝);
 * 非法/缺失时返回 undefined,由调用方落到默认着陆页。
 */
export function resolveReturnTo(search: string): SitePath | undefined {
  const value = new URLSearchParams(search).get('return_to');
  if (value === null || value === '' || !value.startsWith('/') || value.startsWith('//')) {
    return undefined;
  }
  return value;
}

/**
 * 登录后的默认着陆页(legacy defaultLoginLanding 规则:
 * src/lib/auth/roles.ts —— 生存服管理角色进 /admin,其余玩家进 /user)。
 * is_admin 仅驱动 UI 去向,服务端每个守卫独立复核(契约 session.ts)。
 */
export function defaultLanding(me: MeData): SitePath {
  return me.user.is_admin ? appUrl('admin-app') : appUrl('user-app');
}

/** 登录成功后的最终去向:return_to 优先,其次角色默认着陆页。 */
export function loginLanding(me: MeData, search: string): SitePath {
  return resolveReturnTo(search) ?? defaultLanding(me);
}

/** 已登录用户访问 guest-only 页面(登录/注册/密码)时的送回地址。 */
export function guestRedirectTarget(me: MeData): SitePath {
  return defaultLanding(me);
}

/** 登录页 URL(带 return_to 时透传,供跨页跳转保持回跳链路)。 */
export function loginUrl(search?: string): SitePath {
  const returnTo = search === undefined ? undefined : resolveReturnTo(search);
  return returnTo === undefined
    ? appUrl('portal', '/login')
    : appUrl('portal', '/login', { return_to: returnTo });
}
